/**
 * Which homes a Listings section asks for (Dawn, 2026-10-21): its "Show" (featured, just listed,
 * just reduced, recently sold, under contract, open houses), "Whose homes" (office first, office
 * only, area) and its ZIP codes. All three are props on the section itself. The host (tenant-sites,
 * the Studio preview) fetches /mls/featured once per distinct feed and hands the homes over keyed
 * by listingsFeedKey, so two sections on one page show their own homes.
 *
 * Mirrored by the gateway's lib/featured-filters.ts (the values /mls/featured accepts).
 */
export const LISTINGS_SHOWS = ['featured', 'just_listed', 'just_reduced', 'recently_sold', 'under_contract', 'open_houses'] as const;
export type ListingsShow = (typeof LISTINGS_SHOWS)[number];
export const LISTINGS_WHOSE = ['office_first', 'office_only', 'area'] as const;
export type ListingsWhose = (typeof LISTINGS_WHOSE)[number];

/** The words for each Show, as the Edit form and the card badge say them. */
export const LISTINGS_SHOW_LABELS: Record<ListingsShow, string> = {
  featured: 'Featured',
  just_listed: 'Just Listed',
  just_reduced: 'Just Reduced',
  recently_sold: 'Recently Sold',
  under_contract: 'Under Contract',
  open_houses: 'Open Houses',
};

export const LISTINGS_WHOSE_LABELS: Record<ListingsWhose, string> = {
  office_first: 'Our office first, then nearby',
  office_only: 'Our office only',
  area: "Everyone's, in these ZIP codes",
};

export interface ListingsFeedQuery {
  show: ListingsShow;
  whose: ListingsWhose;
  zips: string[];
}

/** "33408, 33410" (or an array) → the valid five-digit ZIPs, de-duplicated, at most ten. */
export function listingsZipList(raw: unknown): string[] {
  const parts = Array.isArray(raw) ? raw.map((z) => String(z ?? '')) : String(raw ?? '').split(/[\s,;]+/);
  return Array.from(new Set(parts.map((z) => z.trim()).filter((z) => /^\d{5}$/.test(z)))).slice(0, 10);
}

export function listingsShowOf(props: unknown): ListingsShow {
  const v = String((props as { show?: unknown } | null | undefined)?.show ?? '');
  return (LISTINGS_SHOWS as readonly string[]).includes(v) ? (v as ListingsShow) : 'featured';
}

export function listingsWhoseOf(props: unknown): ListingsWhose {
  const v = String((props as { whose?: unknown } | null | undefined)?.whose ?? '');
  return (LISTINGS_WHOSE as readonly string[]).includes(v) ? (v as ListingsWhose) : 'office_first';
}

/** What one Listings section asks the gateway for. */
export function listingsFeedQuery(props: unknown): ListingsFeedQuery {
  return {
    show: listingsShowOf(props),
    whose: listingsWhoseOf(props),
    zips: listingsZipList((props as { fillZips?: unknown } | null | undefined)?.fillZips),
  };
}

/** One key per distinct feed: sections asking for the same homes share one fetch. */
export function listingsFeedKey(props: unknown): string {
  const q = listingsFeedQuery(props);
  return `${q.show}|${q.whose}|${q.zips.join(',')}`;
}

/** The /mls/featured query string for a feed ('' for the plain featured feed, as before). */
export function listingsFeedSearch(q: ListingsFeedQuery): string {
  const params: string[] = [];
  if (q.zips.length > 0) params.push(`zips=${q.zips.join(',')}`);
  if (q.show !== 'featured') params.push(`show=${q.show}`);
  if (q.whose !== 'office_first') params.push(`whose=${q.whose}`);
  return params.join('&');
}

/** The distinct feeds a page's visible Listings sections ask for, first section first. */
export function pageListingsFeeds(blocks: ReadonlyArray<{ type: string; hidden?: boolean; props?: unknown }> | null | undefined): Array<{ key: string; query: ListingsFeedQuery }> {
  const out = new Map<string, ListingsFeedQuery>();
  for (const b of blocks ?? []) {
    if (b.type !== 'listings' || b.hidden) continue;
    const key = listingsFeedKey(b.props);
    if (!out.has(key)) out.set(key, listingsFeedQuery(b.props));
  }
  return [...out].map(([key, query]) => ({ key, query }));
}
